<template>
  <div
    v-if="pageLoaded"
    class="h-[100dvh] overflow-hidden"
  >
    <!-- Global Style HTML -->
    <button class="estage-btn d-none" />
    <!-- End Global Style HTML -->

    <global-styles-render v-if="project && !reInitProject" />

    <editor-header
      v-if="project && !reInitProject"
      :can-update="canUpdatePages"
      can-create
    >
      <template #undo-redo>
        <b-btn
          v-b-tooltip.hover
          title="Undo"
          variant="white"
          :disabled="!isActiveUndo"
          class="w-[40px] h-[40px] text-[#6E747A] p-0 rounded-right-0"
          :class="{'hover:!bg-transparent': !isActiveUndo}"
          @click="undo"
        >
          <svg
            width="17"
            height="11"
            viewBox="0 0 17 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.4649 10.7396C16.6349 10.704 16.7547 10.5567 16.7547 10.3867C16.7547 4.51518 9.7847 3.26446 8.16565 3.05318V0.35908C8.16565 0.226318 8.0928 0.10489 7.97541 0.0409374C7.85641 -0.0205864 7.71556 -0.0116817 7.60627 0.0603659L0.157034 5.07456C0.059082 5.14094 -1.52588e-05 5.25103 -1.52588e-05 5.37327C-1.52588e-05 5.49227 0.059082 5.60318 0.157034 5.66956L7.60222 10.6829C7.71313 10.7558 7.8556 10.7639 7.97218 10.7016C8.09037 10.6384 8.16322 10.5162 8.16322 10.3826V7.48451C9.3346 7.49179 10.3044 7.5436 11.1115 7.64075C14.9851 8.09975 16.0205 10.4255 16.0626 10.5251C16.12 10.6587 16.2512 10.7445 16.3945 10.7445C16.4187 10.7461 16.443 10.7445 16.4649 10.7396Z"
              fill="#6E747A"
            />
          </svg>
        </b-btn>
        <b-btn
          v-b-tooltip.hover
          title="Redo"
          variant="white"
          :disabled="!isActiveRedo"
          class="w-[40px] h-[40px] text-[#6E747A] p-0 rounded-x-0"
          :class="{'hover:!bg-transparent': !isActiveRedo}"
          @click="redo"
        >
          <svg
            width="17"
            height="11"
            viewBox="0 0 17 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.28981 10.7396C0.11981 10.704 0 10.5567 0 10.3867C0 4.51518 6.97 3.26446 8.58905 3.05318V0.35908C8.58905 0.226318 8.6619 0.10489 8.77929 0.0409374C8.89829 -0.0205864 9.03914 -0.0116817 9.14843 0.0603659L16.5977 5.07456C16.6956 5.14094 16.7547 5.25103 16.7547 5.37327C16.7547 5.49227 16.6956 5.60318 16.5977 5.66956L9.15248 10.6829C9.04157 10.7558 8.8991 10.7639 8.78252 10.7016C8.66433 10.6384 8.59148 10.5162 8.59148 10.3826V7.48451C7.4201 7.49179 6.45029 7.5436 5.64319 7.64075C1.76962 8.09975 0.734238 10.4255 0.692143 10.5251C0.634667 10.6587 0.503524 10.7445 0.360238 10.7445C0.335952 10.7461 0.311667 10.7445 0.28981 10.7396Z"
              fill="#6E747A"
            />
          </svg>
        </b-btn>
      </template>
    </editor-header>

    <div v-if="project && !reInitProject">
      <div class="flex w-full">
        <editor-toolbar :readonly="!canUpdatePages" />
        <div
          class="w-full"
        >
          <stack-elements v-if="canUpdatePages" />
          <div
            :class="'editor-container-' + (tablet ? 'md' : resolution)"
            :style="{height: `calc(100vh - ${canUpdatePages ? 112 : 72}px)`}"
            class="editor-container max-h-full w-full flex flex-column position-relative"
          >
            <div class="scrollbale-container overflow-auto">
              <div
                class="flex justify-center"
              >
                <div
                  v-resizable
                  :class="['resolution-' + (tablet ? 'md' : resolution)]"
                  :style="{background: globalStyles.backgroundColor}"
                  class="es-root select-none min-w-[300px] overflow-hidden web position-relative pt-[2px] px-[2px]"
                >
                  <div
                    :style="{...backgroundColorSubject(globalStyles)}"
                    class="image-background"
                  />

                  <div v-if="page">
                    <div
                      :style="{...backgroundColorSubject(page.settings)}"
                      class="image-background"
                    />

                    <div
                      :style="{...backgroundImageSubject(globalStyles)}"
                      class="image-background"
                    />

                    <div
                      :style="{...backgroundImageOverlaySubject(globalStyles)}"
                      class="image-background"
                    />

                    <div
                      :style="{...backgroundImageSubject(page.settings)}"
                      class="image-background"
                    />
                    <div
                      :style="{...backgroundImageOverlaySubject(page.settings)}"
                      class="image-background"
                    />
                  </div>

                  <!-- Top Promo Bar -->
                  <promo-bar-docked-wrapper @favorite="dockedToFavorite" />
                  <!-- End Top Promo Bar -->

                  <div class="blocks-container">
                    <div class="first-section global-block section-item position-relative z-canvas-sticky">
                      <component
                        :is="globalStyles.navbar.block.component"
                        v-if="globalStyles.navbar.block && !hideHeader"
                        :section="globalStyles.navbar.block"
                        @delete="deleteGlobalBlock(globalStyles.navbar.block)"
                        @favorite="node => favoriteBlock = {section: JSON.parse(JSON.stringify(globalStyles.navbar.block)), node}"
                        @add-subject="() => addBlock(-1)"
                      />
                    </div>

                    <div class="page-content-wrapper">
                      <div
                        v-if="showDragPlaceholder"
                        class="empty-icon position-absolute d-flex align-items-center justify-content-center h-100 w-100 cursor-pinter"
                        @click="() => addBlock(0)"
                      >
                        <svg
                          fill="none"
                          height="170"
                          viewBox="0 0 213 170"
                          width="213"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.805 165C12.6783 165 12.5643 164.956 12.463 164.867C12.3743 164.766 12.33 164.652 12.33 164.525V152.175C12.33 152.036 12.3743 151.922 12.463 151.833C12.5643 151.744 12.6783 151.7 12.805 151.7H17.745C19.607 151.7 21.051 152.143 22.077 153.03C23.103 153.917 23.6413 155.202 23.692 156.887C23.7047 157.254 23.711 157.742 23.711 158.35C23.711 158.958 23.7047 159.439 23.692 159.794C23.6287 161.555 23.103 162.866 22.115 163.727C21.1397 164.576 19.7147 165 17.84 165H12.805ZM17.745 162.264C18.581 162.264 19.1953 162.068 19.588 161.675C19.9807 161.27 20.1897 160.63 20.215 159.756C20.2403 159.389 20.253 158.914 20.253 158.331C20.253 157.748 20.2403 157.28 20.215 156.925C20.1897 156.076 19.9617 155.449 19.531 155.044C19.113 154.639 18.486 154.436 17.65 154.436H15.75V162.264H17.745ZM26.1966 165C26.0573 165 25.9433 164.956 25.8546 164.867C25.7659 164.778 25.7216 164.664 25.7216 164.525V155.595C25.7216 155.456 25.7659 155.342 25.8546 155.253C25.9433 155.164 26.0573 155.12 26.1966 155.12H28.3816C28.5209 155.12 28.6349 155.171 28.7236 155.272C28.8249 155.361 28.8756 155.468 28.8756 155.595V156.355C29.5723 155.532 30.5096 155.12 31.6876 155.12H32.5236C32.6629 155.12 32.7769 155.164 32.8656 155.253C32.9543 155.342 32.9986 155.456 32.9986 155.595V157.552C32.9986 157.679 32.9543 157.793 32.8656 157.894C32.7769 157.983 32.6629 158.027 32.5236 158.027H30.6806C30.1613 158.027 29.7559 158.173 29.4646 158.464C29.1859 158.743 29.0466 159.142 29.0466 159.661V164.525C29.0466 164.664 28.9959 164.778 28.8946 164.867C28.8059 164.956 28.6919 165 28.5526 165H26.1966ZM37.1465 165.19C36.4752 165.19 35.8609 165.063 35.3035 164.81C34.7589 164.544 34.3282 164.189 34.0115 163.746C33.7075 163.29 33.5555 162.79 33.5555 162.245C33.5555 161.371 33.9102 160.668 34.6195 160.136C35.3415 159.604 36.3295 159.237 37.5835 159.034L39.9585 158.673V158.407C39.9585 157.926 39.8572 157.571 39.6545 157.343C39.4519 157.115 39.1099 157.001 38.6285 157.001C38.3372 157.001 38.0965 157.052 37.9065 157.153C37.7165 157.254 37.5202 157.394 37.3175 157.571C37.1402 157.723 37.0072 157.824 36.9185 157.875C36.8805 157.976 36.8109 158.027 36.7095 158.027H34.6385C34.5119 158.027 34.4042 157.989 34.3155 157.913C34.2395 157.824 34.2079 157.723 34.2205 157.609C34.2332 157.267 34.3979 156.887 34.7145 156.469C35.0439 156.051 35.5379 155.69 36.1965 155.386C36.8679 155.082 37.6912 154.93 38.6665 154.93C40.2245 154.93 41.3772 155.278 42.1245 155.975C42.8719 156.659 43.2455 157.584 43.2455 158.749V164.525C43.2455 164.652 43.2012 164.766 43.1125 164.867C43.0239 164.956 42.9099 165 42.7705 165H40.5665C40.4399 165 40.3259 164.956 40.2245 164.867C40.1359 164.766 40.0915 164.652 40.0915 164.525V163.86C39.8129 164.253 39.4202 164.576 38.9135 164.829C38.4195 165.07 37.8305 165.19 37.1465 165.19ZM38.0395 163.024C38.6222 163.024 39.0909 162.834 39.4455 162.454C39.8129 162.074 39.9965 161.517 39.9965 160.782V160.516L38.3815 160.801C37.2415 161.004 36.6715 161.403 36.6715 161.998C36.6715 162.315 36.8045 162.568 37.0705 162.758C37.3365 162.935 37.6595 163.024 38.0395 163.024ZM50.2346 169.18C49.0312 169.18 48.0559 168.984 47.3086 168.591C46.5612 168.198 46.0229 167.749 45.6936 167.242C45.3769 166.735 45.2186 166.311 45.2186 165.969C45.2186 165.855 45.2692 165.754 45.3706 165.665C45.4719 165.576 45.5859 165.532 45.7126 165.532H48.1256C48.2522 165.532 48.3472 165.557 48.4106 165.608C48.4866 165.671 48.5562 165.773 48.6196 165.912C48.7462 166.203 48.9172 166.431 49.1326 166.596C49.3479 166.773 49.6836 166.862 50.1396 166.862C50.7222 166.862 51.1529 166.716 51.4316 166.425C51.7102 166.146 51.8496 165.671 51.8496 165V163.727C51.2036 164.436 50.2789 164.791 49.0756 164.791C47.7962 164.791 46.8146 164.417 46.1306 163.67C45.4592 162.91 45.0919 161.814 45.0286 160.383L45.0096 159.87C45.0096 158.337 45.3516 157.134 46.0356 156.26C46.7196 155.373 47.7329 154.93 49.0756 154.93C49.7216 154.93 50.2916 155.057 50.7856 155.31C51.2796 155.551 51.6976 155.88 52.0396 156.298V155.595C52.0396 155.456 52.0839 155.342 52.1726 155.253C52.2739 155.164 52.3879 155.12 52.5146 155.12H54.6996C54.8389 155.12 54.9529 155.171 55.0416 155.272C55.1429 155.361 55.1936 155.468 55.1936 155.595V164.734C55.1936 166.178 54.7629 167.28 53.9016 168.04C53.0402 168.8 51.8179 169.18 50.2346 169.18ZM50.1016 162.321C50.6716 162.321 51.0959 162.15 51.3746 161.808C51.6532 161.453 51.8116 161.01 51.8496 160.478C51.8622 160.389 51.8686 160.193 51.8686 159.889C51.8686 159.598 51.8622 159.408 51.8496 159.319C51.8242 158.787 51.6659 158.35 51.3746 158.008C51.0959 157.653 50.6716 157.476 50.1016 157.476C49.5062 157.476 49.0756 157.653 48.8096 158.008C48.5436 158.363 48.3979 158.85 48.3726 159.471L48.3536 159.889C48.3536 160.636 48.4802 161.232 48.7336 161.675C48.9869 162.106 49.4429 162.321 50.1016 162.321ZM61.9515 165C61.8121 165 61.6981 164.956 61.6095 164.867C61.5208 164.778 61.4765 164.664 61.4765 164.525V155.595C61.4765 155.456 61.5208 155.342 61.6095 155.253C61.6981 155.164 61.8121 155.12 61.9515 155.12H64.1365C64.2758 155.12 64.3898 155.164 64.4785 155.253C64.5798 155.342 64.6305 155.456 64.6305 155.595V156.317C64.9725 155.899 65.4095 155.563 65.9415 155.31C66.4861 155.057 67.1005 154.93 67.7845 154.93C68.8991 154.93 69.8048 155.31 70.5015 156.07C71.1981 156.817 71.5465 157.869 71.5465 159.224V164.525C71.5465 164.652 71.5021 164.766 71.4135 164.867C71.3248 164.956 71.2108 165 71.0715 165H68.6775C68.5508 165 68.4368 164.956 68.3355 164.867C68.2468 164.766 68.2025 164.652 68.2025 164.525V159.338C68.2025 158.743 68.0568 158.287 67.7655 157.97C67.4741 157.641 67.0561 157.476 66.5115 157.476C65.9795 157.476 65.5615 157.641 65.2575 157.97C64.9535 158.287 64.8015 158.743 64.8015 159.338V164.525C64.8015 164.652 64.7571 164.766 64.6685 164.867C64.5798 164.956 64.4658 165 64.3265 165H61.9515ZM78.2455 165.19C76.7255 165.19 75.5285 164.778 74.6545 163.955C73.7805 163.132 73.3245 161.935 73.2865 160.364V159.699C73.3371 158.204 73.7995 157.039 74.6735 156.203C75.5601 155.354 76.7445 154.93 78.2265 154.93C79.3031 154.93 80.2088 155.152 80.9435 155.595C81.6908 156.026 82.2481 156.621 82.6155 157.381C82.9955 158.141 83.1855 159.015 83.1855 160.003V160.459C83.1855 160.586 83.1411 160.7 83.0525 160.801C82.9638 160.89 82.8498 160.934 82.7105 160.934H76.6305V161.067C76.6558 161.662 76.8015 162.144 77.0675 162.511C77.3335 162.878 77.7198 163.062 78.2265 163.062C78.5431 163.062 78.8028 162.999 79.0055 162.872C79.2081 162.733 79.3918 162.568 79.5565 162.378C79.6705 162.239 79.7591 162.156 79.8225 162.131C79.8985 162.093 80.0125 162.074 80.1645 162.074H82.5205C82.6345 162.074 82.7295 162.112 82.8055 162.188C82.8941 162.251 82.9385 162.34 82.9385 162.454C82.9385 162.783 82.7485 163.163 82.3685 163.594C82.0011 164.025 81.4628 164.398 80.7535 164.715C80.0441 165.032 79.2081 165.19 78.2455 165.19ZM79.8415 159.053V159.015C79.8415 158.394 79.6958 157.907 79.4045 157.552C79.1258 157.185 78.7331 157.001 78.2265 157.001C77.7325 157.001 77.3398 157.185 77.0485 157.552C76.7698 157.907 76.6305 158.394 76.6305 159.015V159.053H79.8415ZM87.4248 165C87.2475 165 87.1082 164.962 87.0068 164.886C86.9182 164.797 86.8485 164.677 86.7978 164.525L84.0618 155.728L84.0238 155.538C84.0238 155.424 84.0682 155.329 84.1568 155.253C84.2455 155.164 84.3468 155.12 84.4608 155.12H86.4178C86.5698 155.12 86.6965 155.164 86.7978 155.253C86.8992 155.329 86.9625 155.418 86.9878 155.519L88.5838 160.953L90.2938 155.538C90.3192 155.437 90.3825 155.342 90.4838 155.253C90.5852 155.164 90.7182 155.12 90.8828 155.12H92.1368C92.3015 155.12 92.4345 155.164 92.5358 155.253C92.6372 155.342 92.7005 155.437 92.7258 155.538L94.4358 160.953L96.0318 155.519C96.0572 155.418 96.1205 155.329 96.2218 155.253C96.3232 155.164 96.4498 155.12 96.6018 155.12H98.5588C98.6728 155.12 98.7742 155.164 98.8628 155.253C98.9515 155.329 98.9958 155.424 98.9958 155.538L98.9578 155.728L96.2218 164.525C96.1712 164.677 96.0952 164.797 95.9938 164.886C95.9052 164.962 95.7722 165 95.5948 165H93.8848C93.5555 165 93.3465 164.842 93.2578 164.525L91.5098 159.319L89.7618 164.525C89.6732 164.842 89.4642 165 89.1348 165H87.4248ZM110.25 165.19C108.983 165.19 108.002 164.766 107.305 163.917V164.525C107.305 164.652 107.261 164.766 107.172 164.867C107.083 164.956 106.969 165 106.83 165H104.683C104.556 165 104.442 164.956 104.341 164.867C104.252 164.766 104.208 164.652 104.208 164.525V151.985C104.208 151.846 104.252 151.732 104.341 151.643C104.442 151.554 104.556 151.51 104.683 151.51H107.001C107.14 151.51 107.254 151.554 107.343 151.643C107.432 151.732 107.476 151.846 107.476 151.985V156.051C108.185 155.304 109.11 154.93 110.25 154.93C111.479 154.93 112.448 155.329 113.157 156.127C113.866 156.925 114.246 158.008 114.297 159.376C114.31 159.541 114.316 159.769 114.316 160.06C114.316 160.339 114.31 160.56 114.297 160.725C114.234 162.144 113.854 163.246 113.157 164.031C112.46 164.804 111.491 165.19 110.25 165.19ZM109.224 162.644C109.819 162.644 110.244 162.467 110.497 162.112C110.763 161.757 110.915 161.27 110.953 160.649C110.978 160.396 110.991 160.199 110.991 160.06C110.991 159.921 110.978 159.724 110.953 159.471C110.915 158.85 110.763 158.363 110.497 158.008C110.244 157.653 109.819 157.476 109.224 157.476C108.667 157.476 108.242 157.647 107.951 157.989C107.66 158.331 107.501 158.762 107.476 159.281L107.457 159.984L107.476 160.706C107.514 161.276 107.666 161.745 107.932 162.112C108.211 162.467 108.641 162.644 109.224 162.644ZM116.669 165C116.543 165 116.429 164.956 116.327 164.867C116.239 164.766 116.194 164.652 116.194 164.525V151.985C116.194 151.846 116.239 151.732 116.327 151.643C116.429 151.554 116.543 151.51 116.669 151.51H118.911C119.051 151.51 119.165 151.554 119.253 151.643C119.342 151.732 119.386 151.846 119.386 151.985V164.525C119.386 164.664 119.342 164.778 119.253 164.867C119.165 164.956 119.051 165 118.911 165H116.669ZM126.305 165.19C124.734 165.19 123.524 164.816 122.676 164.069C121.827 163.322 121.365 162.27 121.289 160.915C121.276 160.75 121.27 160.465 121.27 160.06C121.27 159.655 121.276 159.37 121.289 159.205C121.365 157.862 121.84 156.817 122.714 156.07C123.588 155.31 124.785 154.93 126.305 154.93C127.837 154.93 129.041 155.31 129.915 156.07C130.789 156.817 131.264 157.862 131.34 159.205C131.352 159.37 131.359 159.655 131.359 160.06C131.359 160.465 131.352 160.75 131.34 160.915C131.264 162.27 130.801 163.322 129.953 164.069C129.104 164.816 127.888 165.19 126.305 165.19ZM126.305 162.872C126.862 162.872 127.274 162.707 127.54 162.378C127.806 162.036 127.958 161.517 127.996 160.82C128.008 160.693 128.015 160.44 128.015 160.06C128.015 159.68 128.008 159.427 127.996 159.3C127.958 158.616 127.799 158.103 127.521 157.761C127.255 157.419 126.849 157.248 126.305 157.248C125.253 157.248 124.696 157.932 124.633 159.3L124.614 160.06L124.633 160.82C124.658 161.517 124.804 162.036 125.07 162.378C125.348 162.707 125.76 162.872 126.305 162.872ZM137.787 165.19C136.356 165.19 135.197 164.81 134.31 164.05C133.423 163.277 132.948 162.207 132.885 160.839L132.866 160.079L132.885 159.3C132.936 157.932 133.404 156.862 134.291 156.089C135.19 155.316 136.356 154.93 137.787 154.93C138.864 154.93 139.769 155.126 140.504 155.519C141.251 155.899 141.802 156.361 142.157 156.906C142.524 157.438 142.721 157.926 142.746 158.369C142.759 158.496 142.714 158.61 142.613 158.711C142.524 158.8 142.41 158.844 142.271 158.844H139.858C139.719 158.844 139.611 158.812 139.535 158.749C139.459 158.686 139.389 158.591 139.326 158.464C139.174 158.071 138.978 157.793 138.737 157.628C138.509 157.463 138.211 157.381 137.844 157.381C136.805 157.381 136.267 158.052 136.229 159.395L136.21 160.117L136.229 160.744C136.254 161.428 136.4 161.935 136.666 162.264C136.945 162.581 137.337 162.739 137.844 162.739C138.237 162.739 138.547 162.657 138.775 162.492C139.003 162.327 139.187 162.049 139.326 161.656C139.389 161.529 139.459 161.434 139.535 161.371C139.611 161.308 139.719 161.276 139.858 161.276H142.271C142.398 161.276 142.505 161.32 142.594 161.409C142.695 161.485 142.746 161.586 142.746 161.713C142.746 162.118 142.569 162.593 142.214 163.138C141.872 163.683 141.327 164.164 140.58 164.582C139.833 164.987 138.902 165.19 137.787 165.19ZM144.928 165C144.801 165 144.687 164.956 144.586 164.867C144.497 164.766 144.453 164.652 144.453 164.525V151.985C144.453 151.846 144.497 151.732 144.586 151.643C144.687 151.554 144.801 151.51 144.928 151.51H147.132C147.271 151.51 147.385 151.554 147.474 151.643C147.563 151.732 147.607 151.846 147.607 151.985V158.35L150.248 155.443C150.273 155.418 150.318 155.373 150.381 155.31C150.457 155.247 150.533 155.202 150.609 155.177C150.685 155.139 150.774 155.12 150.875 155.12H153.421C153.535 155.12 153.63 155.164 153.706 155.253C153.795 155.329 153.839 155.424 153.839 155.538C153.839 155.677 153.788 155.785 153.687 155.861L150.267 159.547L154.162 164.278C154.263 164.379 154.314 164.474 154.314 164.563C154.314 164.69 154.27 164.797 154.181 164.886C154.105 164.962 154.004 165 153.877 165H151.274C151.109 165 150.983 164.975 150.894 164.924C150.818 164.873 150.723 164.791 150.609 164.677L147.607 161.067V164.525C147.607 164.652 147.563 164.766 147.474 164.867C147.385 164.956 147.271 165 147.132 165H144.928ZM159.429 165.19C158.403 165.19 157.542 165.044 156.845 164.753C156.161 164.462 155.655 164.113 155.325 163.708C154.996 163.303 154.831 162.942 154.831 162.625C154.831 162.498 154.876 162.397 154.964 162.321C155.066 162.232 155.173 162.188 155.287 162.188H157.453C157.529 162.188 157.599 162.22 157.662 162.283C157.814 162.384 157.928 162.467 158.004 162.53C158.283 162.733 158.524 162.885 158.726 162.986C158.942 163.075 159.201 163.119 159.505 163.119C159.873 163.119 160.17 163.049 160.398 162.91C160.639 162.758 160.759 162.549 160.759 162.283C160.759 162.068 160.696 161.897 160.569 161.77C160.455 161.643 160.227 161.523 159.885 161.409C159.543 161.282 159.024 161.149 158.327 161.01C157.276 160.795 156.465 160.446 155.895 159.965C155.338 159.471 155.059 158.812 155.059 157.989C155.059 157.47 155.224 156.976 155.553 156.507C155.883 156.038 156.37 155.658 157.016 155.367C157.675 155.076 158.454 154.93 159.353 154.93C160.253 154.93 161.038 155.069 161.709 155.348C162.381 155.627 162.887 155.969 163.229 156.374C163.584 156.767 163.761 157.128 163.761 157.457C163.761 157.571 163.717 157.672 163.628 157.761C163.552 157.85 163.457 157.894 163.343 157.894H161.367C161.266 157.894 161.171 157.862 161.082 157.799C160.918 157.71 160.772 157.615 160.645 157.514C160.43 157.349 160.227 157.223 160.037 157.134C159.847 157.045 159.613 157.001 159.334 157.001C159.005 157.001 158.739 157.077 158.536 157.229C158.346 157.381 158.251 157.577 158.251 157.818C158.251 158.008 158.302 158.166 158.403 158.293C158.517 158.42 158.745 158.546 159.087 158.673C159.442 158.787 159.961 158.907 160.645 159.034C161.874 159.249 162.767 159.629 163.324 160.174C163.894 160.719 164.179 161.358 164.179 162.093C164.179 163.03 163.761 163.784 162.925 164.354C162.089 164.911 160.924 165.19 159.429 165.19ZM170.385 165C170.258 165 170.144 164.956 170.043 164.867C169.954 164.766 169.91 164.652 169.91 164.525V151.985C169.91 151.846 169.954 151.732 170.043 151.643C170.144 151.554 170.258 151.51 170.385 151.51H172.817C172.956 151.51 173.07 151.554 173.159 151.643C173.248 151.732 173.292 151.846 173.292 151.985V156.222C174.052 155.361 175.053 154.93 176.294 154.93C177.029 154.93 177.681 155.095 178.251 155.424C178.834 155.753 179.29 156.241 179.619 156.887C179.948 157.533 180.113 158.312 180.113 159.224V164.525C180.113 164.652 180.069 164.766 179.98 164.867C179.891 164.956 179.777 165 179.638 165H177.187C177.06 165 176.946 164.956 176.845 164.867C176.756 164.766 176.712 164.652 176.712 164.525V159.338C176.712 158.743 176.566 158.287 176.275 157.97C175.996 157.641 175.585 157.476 175.04 157.476C174.495 157.476 174.065 157.641 173.748 157.97C173.444 158.287 173.292 158.743 173.292 159.338V164.525C173.292 164.664 173.248 164.778 173.159 164.867C173.07 164.956 172.956 165 172.817 165H170.385ZM186.827 165.19C185.307 165.19 184.11 164.778 183.236 163.955C182.362 163.132 181.906 161.935 181.868 160.364V159.699C181.919 158.204 182.381 157.039 183.255 156.203C184.142 155.354 185.326 154.93 186.808 154.93C187.885 154.93 188.791 155.152 189.525 155.595C190.273 156.026 190.83 156.621 191.197 157.381C191.577 158.141 191.767 159.015 191.767 160.003V160.459C191.767 160.586 191.723 160.7 191.634 160.801C191.546 160.89 191.432 160.934 191.292 160.934H185.212V161.067C185.238 161.662 185.383 162.144 185.649 162.511C185.915 162.878 186.302 163.062 186.808 163.062C187.125 163.062 187.385 162.999 187.587 162.872C187.79 162.733 187.974 162.568 188.138 162.378C188.252 162.239 188.341 162.156 188.404 162.131C188.48 162.093 188.594 162.074 188.746 162.074H191.102C191.216 162.074 191.311 162.112 191.387 162.188C191.476 162.251 191.52 162.34 191.52 162.454C191.52 162.783 191.33 163.163 190.95 163.594C190.583 164.025 190.045 164.398 189.335 164.715C188.626 165.032 187.79 165.19 186.827 165.19ZM188.423 159.053V159.015C188.423 158.394 188.278 157.907 187.986 157.552C187.708 157.185 187.315 157.001 186.808 157.001C186.314 157.001 185.922 157.185 185.63 157.552C185.352 157.907 185.212 158.394 185.212 159.015V159.053H188.423ZM194.024 165C193.884 165 193.77 164.956 193.682 164.867C193.593 164.778 193.549 164.664 193.549 164.525V155.595C193.549 155.456 193.593 155.342 193.682 155.253C193.77 155.164 193.884 155.12 194.024 155.12H196.209C196.348 155.12 196.462 155.171 196.551 155.272C196.652 155.361 196.703 155.468 196.703 155.595V156.355C197.399 155.532 198.337 155.12 199.515 155.12H200.351C200.49 155.12 200.604 155.164 200.693 155.253C200.781 155.342 200.826 155.456 200.826 155.595V157.552C200.826 157.679 200.781 157.793 200.693 157.894C200.604 157.983 200.49 158.027 200.351 158.027H198.508C197.988 158.027 197.583 158.173 197.292 158.464C197.013 158.743 196.874 159.142 196.874 159.661V164.525C196.874 164.664 196.823 164.778 196.722 164.867C196.633 164.956 196.519 165 196.38 165H194.024ZM206.644 165.19C205.124 165.19 203.927 164.778 203.053 163.955C202.179 163.132 201.723 161.935 201.685 160.364V159.699C201.736 158.204 202.198 157.039 203.072 156.203C203.959 155.354 205.143 154.93 206.625 154.93C207.702 154.93 208.607 155.152 209.342 155.595C210.089 156.026 210.647 156.621 211.014 157.381C211.394 158.141 211.584 159.015 211.584 160.003V160.459C211.584 160.586 211.54 160.7 211.451 160.801C211.362 160.89 211.248 160.934 211.109 160.934H205.029V161.067C205.054 161.662 205.2 162.144 205.466 162.511C205.732 162.878 206.118 163.062 206.625 163.062C206.942 163.062 207.201 162.999 207.404 162.872C207.607 162.733 207.79 162.568 207.955 162.378C208.069 162.239 208.158 162.156 208.221 162.131C208.297 162.093 208.411 162.074 208.563 162.074H210.919C211.033 162.074 211.128 162.112 211.204 162.188C211.293 162.251 211.337 162.34 211.337 162.454C211.337 162.783 211.147 163.163 210.767 163.594C210.4 164.025 209.861 164.398 209.152 164.715C208.443 165.032 207.607 165.19 206.644 165.19ZM208.24 159.053V159.015C208.24 158.394 208.094 157.907 207.803 157.552C207.524 157.185 207.132 157.001 206.625 157.001C206.131 157.001 205.738 157.185 205.447 157.552C205.168 157.907 205.029 158.394 205.029 159.015V159.053H208.24Z"
                            fill="#CDD1DA"
                          />
                          <rect
                            fill="white"
                            height="65.0612"
                            rx="3.5"
                            stroke="#CDD1DA"
                            stroke-width="3"
                            transform="rotate(-26.0708 2.50855 59.1584)"
                            width="83"
                            x="2.50855"
                            y="59.1584"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="34.4566"
                            rx="2"
                            transform="rotate(-25.2799 29.3799 51.2021)"
                            width="47.3073"
                            x="29.3799"
                            y="51.2021"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="48.5623"
                            rx="2"
                            transform="rotate(-25.2799 11 62.4807)"
                            width="17.5176"
                            x="11"
                            y="62.4807"
                          />
                          <rect
                            fill="white"
                            height="79.4969"
                            rx="4"
                            stroke="#CDD1DA"
                            stroke-width="2"
                            transform="rotate(14.8453 99.5908 1.22283)"
                            width="111"
                            x="99.5908"
                            y="1.22283"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="34.4566"
                            rx="2"
                            transform="rotate(14.85 104.831 8)"
                            width="34.4566"
                            x="104.831"
                            y="8"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="4.00471"
                            rx="2"
                            transform="rotate(15 146.337 27)"
                            width="51.7454"
                            x="146.337"
                            y="27"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="4.00471"
                            rx="2"
                            transform="rotate(15 143.036 34.4746)"
                            width="51.7454"
                            x="143.036"
                            y="34.4746"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="4.00471"
                            rx="2"
                            transform="rotate(15 141.036 41.4746)"
                            width="14.7239"
                            x="141.036"
                            y="41.4746"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="4.00471"
                            rx="2"
                            transform="rotate(15 114.036 60)"
                            width="51.7454"
                            x="114.036"
                            y="60"
                          />
                          <rect
                            fill="#CDD1DA"
                            height="4.00471"
                            rx="2"
                            transform="rotate(15 128.036 73)"
                            width="14.7239"
                            x="128.036"
                            y="73"
                          />
                        </svg>
                      </div>
                      <template v-if="!loadingRedoUndo">
                        <draggable
                          v-model="sections"
                          :style="{minHeight: `${!sections.length ? '50vh': '100%'}`}"
                          group="blocks"
                          handle=".move"
                          ghost-class="blocks-sortable-ghost"
                          :scroll="true"
                          :force-fallback="true"
                          fallback-class="section-sortable-fallback"
                          :force-autoscroll-fallback="true"
                          :scroll-sensitivity="60"
                          :bubble-scroll="true"
                          @add="onAddedBlock"
                        >
                          <component
                            :is="section.component"
                            v-for="(section, index) in sections"
                            :key="section.render_id || section.id || index"
                            :class="{'first-section': (index === 0 && (!globalStyles.navbar.block || hideHeader))}"
                            :section="section"
                            class="section-drag"
                            @delete="() => deleteSection(index)"
                            @dublicate="() => dublicateElement(index)"
                            @favorite="node => favoriteBlock = {origSection: getSections[index], section: JSON.parse(JSON.stringify(getSections[index])), node}"
                            @move-to="to => moveTo(index, to)"
                            @add-subject="() => addBlock(index)"
                          />
                        </draggable>
                      </template>
                    </div>

                    <div class="global-block">
                      <component
                        :is="globalStyles.footer.block.component"
                        v-if="globalStyles.footer.block && !hideFooter"
                        :section="globalStyles.footer.block"
                        @delete="deleteGlobalBlock(globalStyles.footer.block)"
                        @favorite="node => favoriteBlock = {section: JSON.parse(JSON.stringify(globalStyles.footer.block)), node}"
                      />
                    </div>

                    <!-- Top Promo Bar -->
                    <promo-bar-docked-wrapper
                      :bottom="true"
                      @favorite="(section, node) => favoriteBlock = {section: JSON.parse(JSON.stringify(section)), node}"
                    />
                    <!-- End Top Promo Bar -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="addFooterDialog"
      body-class="text-center py-5"
      centered
      hide-footer
      hide-header
      size="lg"
    >
      <div class="mt-4">
        <h4 class="h2 font-weight-bold">
          Warning
        </h4>
      </div>

      <div class="my-4">
        <p>
          There is already a section from the footer group on the page. Delete the old section before replacing it with
          the new one.
          If you have menus inside your existing footer, you can save them as "favorites", otherwise, they will be
          removed.
        </p>
      </div>

      <div class="d-flex align-items-center justify-content-center">
        <b-btn
          class="btn-wide mr-2"
          variant="danger"
          @click="addFooterDialog = false"
        >
          Got It
        </b-btn>
      </div>
    </b-modal>

    <add-to-favorite-modal
      :favorite="favoriteBlock"
      @submit="favorite"
    />

    <b-modal
      v-model="errorToast"
      title="Your current session has expired"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      hide-footer
    >
      Your current session has expired - please <a
        href="https://auth.estage.com/login"
        target="_blank"
      >log in</a> to continue.
    </b-modal>

    <session-control-modal v-if="project" />

    <div
      v-if="showResizeWarning"
      class="resize-lock-overlay"
    >
      <i
        class="fa fa-times fixed top-10 right-10 cursor-pointer hover:opacity-60"
        @click="showResizeWarning = false"
      />
      Resize your browser window for the best experience
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapState} from 'vuex'
import draggable from 'vuedraggable'
import EditorToolbar from '../editor/navigation/EditorToolbar'
import CodeInjection from '../mixins/CodeInjection'
import EditorHeader from '../editor/header/EditorHeader'
import NavigationSettings from '../mixins/NavigationSettings'
import BackgroundVideo from './utils/background-video/BackgroundVideo'
import PreviewModuleIframeblog from '../../views/blog/PreviewModuleIframeblog'
import DraggableElementsHandler from '../mixins/DraggableElementsHandler'
import AddToFavoriteModal from './components/modals/AddToFavoriteModal'
import StackElements from '@/components/editor/components/StackElements'
import BackgroundMixin from '@/components/mixins/BackgroundMixin'
import GlobalStylesRender from '@/components/builder/utils/GlobalStylesRender'
import PromoBarDockedWrapper from '@/components/builder/sections/promo-bar/PromoBarDockedWrapper'
import Footer from '@/layouts/Footer'
import ResolutionMixin from '@/components/mixins/ResolutionMixin'
import {
  applyPageStyles,
  clonePart,
  insertSectionStyle,
  pageStylesToString,
  pageStylesToStringAsync,
  retriveStyles
} from '@/utils/useStylesheet'
import ViewportUpdate from '@/mixins/viewportUpdate'
import undoRedoSupport from '@/components/mixins/UndoRedoSupport'
import SessionControlModal from '@/components/editor/modals/SessionControlModal.vue'

export default {
  name: 'EsRoot',

  components: {
    SessionControlModal,
    Footer,
    PromoBarDockedWrapper,
    GlobalStylesRender,
    StackElements,
    AddToFavoriteModal,
    PreviewModuleIframeblog,
    BackgroundVideo,
    EditorHeader,
    EditorToolbar,
    draggable
  },

  mixins: [
    ResolutionMixin,
    CodeInjection,
    NavigationSettings,
    DraggableElementsHandler,
    BackgroundMixin,
    ViewportUpdate,
    undoRedoSupport
  ],

  data () {
    return {
      interval: null,
      interval3: null,
      interval4: null,
      addFooterDialog: false,
      changed: false,
      timer: null,
      favoriteBlock: {},
      selection: null,
      active: true,
      errorToast: false,
      showResizeWarning: true,
      pageLoaded: false,
      reInitProject: false,
      hasPendingChanges: false,
      suppressBeforeUnloadOnce: false
    }
  },

  computed: {
    currentPageId () {
      return this.$route.query.page
    },
    getTargetContent: {
      get () {
        return {
          header: this.globalStyles.navbar.block,
          content: this.getSections,
          footer: this.globalStyles.footer.block,
          css: pageStylesToString()
        }
      },
      set (val) {
        this.$store.dispatch('sections/setSections', val.content)
        this.$set(this.globalStyles.navbar, 'block', val.header)
        this.$set(this.globalStyles.footer, 'block', val.footer)
      }
    },
    unpin () {
      return this.$store.state.editor.unpin
    },
    hideFooter () {
      return !!this.page?.settings?.hideFooter
    },
    hideHeader () {
      return !!this.page?.settings?.hideHeader
    },
    showDragPlaceholder () {
      return !this.sections.length
    },
    ...mapState('editor', {
      theme: state => state.theme,
      globalStyles: state => state.globalStyles,
      resolution: state => state.resolution,
      tablet: state => state.tablet
    }),
    ...mapState('pages', {
      page: state => state.page
    }),
    ...mapGetters('sections', {
      getSections: 'getSections',
      navbar: 'getNavbar',
      footer: 'getFooter'
    }),
    ...mapState('font', {
      fonts: state => state.fonts
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapState('sections', {
      allSections: state => state.sections
    }),
    ...mapState('projects', {
      project: state => state.project,
      publishing: state => state.publishing
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    canUpdatePages () {
      return this.checkPermissions('pages.update')
    },
    sections: {
      get () {
        return this.getSections
      },
      set (val) {
        this.$store.dispatch('sections/setSections', val)
      }
    }
  },

  watch: {
    currentPageId () {
      this.resetUndoRedo()
    },
    sectionsCount (val) {
      if (!val) {
        setTimeout(() => {
          this.addBlock(0)
        }, 400)
      }
    },
    sections: {
      deep: true,
      handler: function () {
        clearTimeout(this.timer)
        this.timer = setTimeout(() => {
          window.dispatchEvent(new Event('contentChanged'))
        }, 100)
        this.hasPendingChanges = true
      }
    }
  },

  async mounted () {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    window.addEventListener('contentChanged', this.markDirty)
    window.addEventListener('estage:suppress-beforeunload-once', this.suppressBeforeUnloadPromptOnce)

    await this.$store.dispatch('projects/hasAccessToPage', 'pages')

    axios.get(`/api/estage-modules/check-subscription?project_id=${this.$route.params.id}&user_id=${this.user.id}`)
      .then(({data}) => {
        this.$store.state.editor.chatSubscription = data.data
      })

    // axios.get(`https://api.estage.com/api/user/subscriptions-by-project/${this.project.id}?only_business_plus=1`)
    //   .then(({data}) => {
    //     this.$store.state.editor.communityPlusSubscription = data.data.business_plus
    //   })
    //
    // if (_.includes(['BUSINESS_PLUS_M', 'BUSINESS_PLUS_Y'], this.user.user_subscription?.plan?.code)) {
    //   this.$store.state.editor.businessPlusSubscription = true
    // } else {
    //   axios.get('https://api.estage.com/api/user/profile')
    //     .then(({data}) => {
    //       this.$store.state.editor.businessPlusSubscription = data.data.business_plus
    //     })
    // }

    if (!this.sections.length) {
      this.addBlock(0)
    }

    this.appendStyles()

    this.$store.commit('historyChanges/SET_DISABLE', false)

    this.interval3 = setInterval(() => {
      axios.get('/api/auth/is-valid-token')
        .then(({data}) => {
          this.errorToast = !data.valid
        })
    }, 10000)

    // this.interval4 = setInterval(() => {
    //   this.$store.dispatch('pages/backup').then(() => {
    //     this.markClean()
    //   })
    // }, 900000)

    await this.$store.dispatch('font/fetchProjectFonts')

    setTimeout(() => {
      this.initUndoRedoSupport()
    }, 5000)
  },

  async created () {
    const importPages = this.$route.query.import_pages
    if (!this.$store.state.pages.page.id) {
      if (this.$route.query.page && !isNaN(parseInt(this.$route.query.page))) {
        await this.$store.dispatch('pages/loadPage', this.$route.query.page)
      } else {
        await this.$store.dispatch('pages/loadHome', '')
      }
    }

    this.$store.commit('editor/SET_TABLET', false)
    this.$store.commit('editor/SET_RESOLUTION', 'lg')

    this.$store.commit('editor/CLEAR_ELEMENTS_IN_STACK')
    this.go('sections')

    await this.$store.dispatch('projects/rawStyles')

    switch (this.theme) {
    case 'demo':
      require('@builder/assets/scss/builder/themes/_demo.scss')
      break
    }

    if (importPages) {
      await this.importPages(importPages)
    }

    this.pageLoaded = true
    this.hasPendingChanges = false
  },

  destroyed () {
    // this.unsubscribe()
    clearInterval(this.interval)
    clearInterval(this.interval3)
    // clearInterval(this.interval4)
    this.$store.commit('historyChanges/CLEAR_HARD')
  },

  beforeDestroy () {
    document.body.removeAttribute('onbeforeunload')
    this.stopUndoRedo()
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    window.removeEventListener('contentChanged', this.markDirty)
    window.removeEventListener('estage:suppress-beforeunload-once', this.suppressBeforeUnloadPromptOnce)
  },

  beforeRouteLeave (to, from, next) {
    if (this.canUpdatePages) {
      VEvent.fire('loader', true)
      this.$store.dispatch('projects/save')
        .then(() => {
          this.hasPendingChanges = false
        })
        .catch((e) => {
          console.error(e)
        })
        .finally(() => {
          next()
          setTimeout(() => {
            VEvent.fire('loader', false)
          }, 1200)
        })
    } else {
      next()
    }
  },

  methods: {
    handleBeforeUnload (e) {
      if (this.suppressBeforeUnloadOnce) {
        this.suppressBeforeUnloadOnce = false
        return
      }

      if (!this.hasPendingChanges) return
      e.preventDefault()
      e.returnValue = ''
    },
    suppressBeforeUnloadPromptOnce () {
      this.suppressBeforeUnloadOnce = true
      setTimeout(() => {
        this.suppressBeforeUnloadOnce = false
      }, 3000)
    },
    markDirty () {
      this.hasPendingChanges = true
    },
    markClean () {
      this.hasPendingChanges = false
    },
    async importPages (pages) {
      const importParam = pages
      if (!importParam) return

      const hashes = importParam.split(',').filter(Boolean)
      if (!hashes.length) return
      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

      const clearContent = (content, safeMode = false) => {
        if (!content) {
          return '[]'
        }

        let contentDraft = content

        contentDraft = contentDraft.replace(/"id":\d+,?/g, '')
          .replace(/"form_id":\d+,?/g, '')

        if (!safeMode) {
          contentDraft = contentDraft.replace(/"action":".*?",?/g, '')
            .replace(/"customLink":".*?",?/g, '')
        }

        contentDraft = contentDraft.replace(/"actions":\[.*?\](,?)/g, '"actions": []$1')
          .replace(/"action":\{.*?\}(,?)/g, '"action": null$1')

        return contentDraft.replace(/,\}/g, '}')
      }

      try {
        const { data } = await axios.post('api/pages/by-hash', { hash: hashes })
        const sourcePages = data.data || []

        VEvent.fire('loader', true)

        const original_ids = []
        const pagesPayload = []

        for (const page of sourcePages) {
          if (!original_ids.includes(page.project_id)) {
            original_ids.push(page.project_id)

            const pageSettings = JSON.parse(page.project.global_settings).font.addedGoogleFonts
            const fonts = _.map(this.globalStyles.font.addedGoogleFonts, item1 => {
              const match = _.find(pageSettings, { family: item1.family })
              if (match) {
                return {
                  family: item1.family,
                  variants: _.uniq(_.concat(item1.variants, match.variants))
                    .sort((a, b) => a - b)
                    .map(weight => weight.toString())
                }
              }
              return item1
            })

            pageSettings.forEach(font => {
              const match = _.find(fonts, { family: font.family })
              if (!match) {
                fonts.push({
                  family: font.family,
                  variants: font.variants
                })
              }
            })

            this.globalStyles.font.addedGoogleFonts = fonts
          }

          const importedPage = {
            slug: page.slug,
            title: page.title,
            is_home: false,
            meta_title: page.meta_title,
            page_status: page.page_status,
            content_draft: clearContent(page.content_draft, page.save_mode),
            content: clearContent(page.content, page.save_mode),
            code_injection: page.code_injection,
            settings: page.settings,
            css_file: page.css_file ? page.css_file : page.project.css_file,
            css_file_draft: page.css_file_draft ? page.css_file_draft : page.project.css_file_draft,
            css_hash: page.new_hash,
            template_id: page.template_id
          }

          if (page.modals) {
            importedPage.modals = page.modals
          }

          pagesPayload.push(importedPage)

          let css = page.project.draft_css

          if (page.css_file_draft) {
            css = await fetch(page.css_file_draft).then(res => res.text())
          }

          const styles = JSON.parse(
            JSON.stringify(retriveStyles(css, page.css_hash))
          )

          insertSectionStyle(styles)
        }

        const { data: created } = await axios.post(`api/projects/${this.project.id}/pages/mass-creation`, {
          original_ids,
          pages: pagesPayload
        })

        const createdPages = created.data || []

        this.$emit('page-added')

        if (!createdPages.length) {
          VEvent.fire('loader', false)
          this.$swal({
            icon: 'error',
            title: 'Pages not imported',
            text: 'These pages couldn’t be found or you don’t have permission to access them',
            showConfirmButton: false
          })
          return
        }

        this.$swal({
          icon: 'success',
          title: 'Done!',
          text: 'The page have been added.',
          showConfirmButton: false,
          timer: 1500
        })

        // Keep the delayed save + CSS update behavior
        await wait(1500)
        try {
          await this.$store.dispatch('projects/save', {})
          const css = await pageStylesToStringAsync()

          // await axios.put(`api/projects/${this.project.id}/css`, {
          //   css: this.project.css !== null ? this.project.css : css,
          //   draft_css: css
          // })

          await this.$store.dispatch('pages/loadPage', createdPages[0].id)
          await this.$store.dispatch('font/fetchCustomFontsCSS')

          applyPageStyles(css)

          this.reInitProject = true
          await wait(250)
          this.reInitProject = false
        } finally {
          VEvent.fire('loader', false)
        }
      } catch (e) {
        console.error(e)
        VEvent.fire('loader', false)
        this.$swal({
          icon: 'error',
          title: 'Pages not imported',
          text: 'These pages couldn’t be found or you don’t have permission to access them',
          showConfirmButton: false
        })
      } finally {
        this.loading = false
        await this.$store.dispatch('pages/loadPages')
      }
    },
    getTargetCSS () {
      return pageStylesToString(this.subjectStyle)
    },
    dockedToFavorite (data) {
      this.favoriteBlock = {origSection: data.section, section: JSON.parse(clonePart(data.section, data.node)), node: data.node}
    },
    appendStyles () {
      if (!this.globalStyles.hasOwnProperty('linksStyle')) return

      let style = null
      if (document.querySelector('[data-global-link-style]')) {
        style = document.querySelector('[data-global-link-style]')
        style.parentElement.removeChild(style)
      } else {
        style = document.createElement('style')
        style.setAttribute('data-global-link-style', '')
      }

      document.head.appendChild(style)
    },
    deleteGlobalBlock (blockJson) {
      const group = _.get(blockJson, 'group')
      if (!group) return

      const key = String(group).toLowerCase()
      if (!this.globalStyles || !this.globalStyles[key]) return

      this.$delete(this.globalStyles[key], 'block')
    },
    onAddedBlock ({newIndex}) {
      const blockJson = JSON.parse(JSON.stringify(this.sections[newIndex]))

      if (!blockJson.ignoreGroup && this.globalStyles.hasOwnProperty(blockJson.group.toLowerCase())) {
        this.deleteSection(newIndex)
        if (blockJson.group.toLowerCase() === 'footer'
            && this.globalStyles.footer.block) {
          return this.addFooterDialog = true
        }

        this.$set(this.globalStyles[blockJson.group.toLowerCase()], 'block', blockJson)
      }

      if (blockJson.beforeBlock) {
        const insertGlobalBlock = () => {
          this.$set(this.globalStyles[blockJson.beforeBlock.group.toLowerCase()], 'block', null)

          setTimeout(() => {
            this.$set(this.globalStyles[blockJson.beforeBlock.group.toLowerCase()], 'block', blockJson.beforeBlock)
            delete this.sections[newIndex].beforeBlock
          })
        }

        if (!_.isEmpty(_.get(this.globalStyles, [blockJson.beforeBlock.group.toLowerCase(), 'block']))) {
          this.$store.dispatch('confirmationDialog/confirmation', {
            title: `The ${blockJson.beforeBlock.group} block already exists.`,
            description: `Are you sure you want to replace the current ${blockJson.beforeBlock.group.toLowerCase()}?`
          })
            .then(() => insertGlobalBlock())
        } else {
          insertGlobalBlock()
        }
      }

      if (!this.sections[newIndex] || this.sections[newIndex].favId || this.sections[newIndex].group === 'internal-embed-block') return

      this.$store.commit('editor/SET_PAGE', {
        name: 'add-row',
        event: 'click',
        subject: {
          subject: this.sections[newIndex],
          options: {subject: this, originKey: 'custom'}
        }
      })
    },
    favorite (payload) {
      this.$store.dispatch('favorites/addFavorite', {
        favorite: {
          section: payload.favorite,
          group: 'blocks'
        },
        origSection: payload.origSection,
        inGlobal: payload.globally,
        inSharable: payload.sharable,
        node: payload.node
      })
        .finally(() => {
          this.favoriteBlock = {}
        })
    },
    moveTo (index, to) {
      this.$store.commit('sections/MOVE_SECTION', {index, to})
    },
    addBlock (index) {
      this.$store.commit('editor/SET_PAGE', {
        name: 'blocks',
        event: 'click',
        subject: {
          options: {index}
        }
      })
    },
    dublicateElement (section) {
      this.$store.commit('sections/DUBLICATE_SECTION', {
        section: this.sections[section],
        sections: this.sections,
        index: section
      })
    },
    deleteSection (section) {
      this.$store.commit('sections/REMOVE_SECTION', section)
      this.$store.dispatch('editor/resetSettings')
    }
  }
}
</script>

<style lang="scss">

.border-drag {
  border: 3px dashed #CDD1DA;
}

.editor {
  .resolution {
    &-lg {
      width: 100%;
    }

    &-sm {
      width: 390px;
    }

    &-md {
      width: 800px;
    }
  }
}

.editor-container-md,
.editor-container-sm {
  padding-top: 20px;
  padding-bottom: 20px;
}

.blocks-container {
  min-height: 87vh;
}

.empty-icon {
  position: absolute;
  left: 50%;
  transform: translateX(-50%)
}

iframe {
  nav {
    display: none !important;
  }
}

.resize-lock-overlay {
  display: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: var(--z-overlay, 2015);
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .85);
  color: #fff;
  text-align: center;
  font-size: 30px;
}

@media (max-width: 950px) {
  .resize-lock-overlay {
    display: flex;
  }
}
</style>
